/**
 * The board (KPR-1395): a list shown as columns, one per value of an element,
 * a card per item, dragged from column to column to change that element.
 * Optional swimlanes by a second element cut the columns into rows.
 *
 * Pure: no React, no antd, no HTTP. The page decides which elements a board
 * may be drawn by (`boardElements`), this module lays the items out
 * (`layoutBoard`) and says what a move writes (`movePatch`).
 *
 * What a board can be drawn by: an element every card the list can return
 * holds, by name and type, whose value is ONE of a known kind —
 *
 *   choice       a single choice with a list; the columns are its list, in
 *                its own order (the ordered union when several cards list it)
 *   user         a single person; the columns are the people the list holds
 *   card-lookup  a single linked record; the columns are the records it holds
 *
 * — and that a person may type: never a driven or calculated value, a
 * sequence, or a masked element. Anything else (several values, a text, a
 * date) has no column a card could sit in.
 *
 * Every board ends with OTHER: the blank values and, on a choice, a value the
 * list no longer names. Dropping a card there clears the value.
 */
import { choiceListOf } from './choiceValues';
import { normalizeId } from './ids';

export const BOARD_TYPES = ['choice', 'user', 'card-lookup'];
/** The key of the column (and lane) that holds blank and off-list values. */
export const OTHER_KEY = '__other';
export const OTHER_LABEL = 'Other';

const isBlank = value => value === undefined || value === null || value === ''
	|| (Array.isArray(value) && value.length === 0);

/** Can a board be drawn by this one element definition? */
export function boardEligible(def) {
	if (!def || typeof def !== 'object' || !def.name) return false;
	if (!BOARD_TYPES.includes(def.dataType)) return false;
	const options = def.options || {};
	if (options.allowMultiple === true) return false;
	if (options.drivenFrom || options.sequence) return false;
	if (def.dataType === 'choice' && !choiceListOf(def).length) return false;
	return true;
}

const labelOf = def => def?.label?.singular || def?.name || '';

/**
 * The elements a list of these cards can be drawn as a board by: each one
 * every card holds, by name and with the same type, in the first card's
 * definition order. `{ name, label, dataType, defs: Map(cardId -> def) }`.
 * An empty list of cards (nothing known yet) is no element.
 */
export function boardElements(cardIds, getElementsForCard) {
	const ids = [...(cardIds || [])].map(normalizeId).filter(Boolean);
	if (!ids.length || typeof getElementsForCard !== 'function') return [];
	const perCard = ids.map(id => ({ id, defs: (getElementsForCard(id) || []).filter(boardEligible) }));
	if (perCard.some(entry => !entry.defs.length)) return [];
	const [first, ...rest] = perCard;
	const out = [];
	first.defs.forEach((def) => {
		const defs = new Map([[first.id, def]]);
		const shared = rest.every((entry) => {
			const match = entry.defs.find(other => other.name === def.name && other.dataType === def.dataType);
			if (match) defs.set(entry.id, match);
			return Boolean(match);
		});
		if (shared) out.push({ name: def.name, label: labelOf(def), dataType: def.dataType, defs });
	});
	return out;
}

/**
 * The board an address (or a saved filter, or a remembered choice) asks for,
 * read against what the list can be drawn by: `by` falls back to the first
 * element when it is not one of them, and `lanes` is dropped when it is not,
 * or is the column element itself. `null` when the list has no element.
 */
export function resolveBoard(options, { by, lanes } = {}) {
	const list = Array.isArray(options) ? options : [];
	if (!list.length) return null;
	const column = list.find(entry => entry.name === by) || list[0];
	const lane = lanes && lanes !== column.name ? list.find(entry => entry.name === lanes) || null : null;
	return { by: column, lanes: lane };
}

/** One stored value as a column key: a choice's value, a person's or a record's id. */
export function valueKey(element, raw) {
	if (isBlank(raw)) return null;
	const value = Array.isArray(raw) ? raw[0] : raw;
	if (isBlank(value)) return null;
	if (element?.dataType === 'choice') return String(value);
	const id = normalizeId(value);
	return id || null;
}

/** The choices of a choice element across every card it is on, in order, once each. */
function choiceColumns(element) {
	const out = [];
	const seen = new Set();
	[...(element?.defs?.values() || [])].forEach((def) => {
		choiceListOf(def).forEach((choice) => {
			const value = choice !== null && typeof choice === 'object' ? choice.value : choice;
			if (isBlank(value)) return;
			const key = String(value);
			if (seen.has(key)) return;
			seen.add(key);
			out.push({
				key,
				value: key,
				label: (choice && typeof choice === 'object' && choice.label) || key,
				color: (choice && typeof choice === 'object' && choice.color) || null,
				icon: (choice && typeof choice === 'object' && choice.icon) || null,
			});
		});
	});
	return out;
}

/**
 * The columns (or lanes) of one element over these items: a choice's whole
 * list, a person or record each value the items hold (ordered by
 * `labelFor(key)`, then the key), and Other last.
 */
export function columnsFor(element, items, { labelFor = null } = {}) {
	if (!element) return [];
	let columns;
	if (element.dataType === 'choice') {
		columns = choiceColumns(element);
	} else {
		const keys = [];
		(items || []).forEach((item) => {
			const key = valueKey(element, item?.elements?.[element.name]);
			if (key && !keys.includes(key)) keys.push(key);
		});
		const label = key => (typeof labelFor === 'function' ? labelFor(key) || '' : '');
		keys.sort((a, b) => {
			const byLabel = label(a).localeCompare(label(b), undefined, { sensitivity: 'base' });
			return byLabel || a.localeCompare(b);
		});
		columns = keys.map(key => ({ key, value: key, label: label(key) || null, color: null, icon: null }));
	}
	return [...columns, { key: OTHER_KEY, value: '', label: OTHER_LABEL, other: true, color: null, icon: null }];
}

/** Which column of `columns` this item sits in: its value's, else Other. */
export function columnKeyOf(element, item, columns) {
	const key = valueKey(element, item?.elements?.[element?.name]);
	if (key && (columns || []).some(column => column.key === key && !column.other)) return key;
	return OTHER_KEY;
}

/**
 * The items laid out: `{ columns, lanes }`, where `lanes` is one entry per
 * lane (one unnamed lane with no swimlanes), each `{ key, label, ..., cells:
 * Map(columnKey -> items) , count }`, and each column carries its `count`
 * across every lane. The items keep the order they came in (the list's sort).
 */
export function layoutBoard({ items = [], by, lanes = null, labelFor = null } = {}) {
	if (!by) return { columns: [], lanes: [] };
	const columns = columnsFor(by, items, { labelFor: labelFor ? key => labelFor(by, key) : null })
		.map(column => ({ ...column, count: 0 }));
	const laneList = lanes
		? columnsFor(lanes, items, { labelFor: labelFor ? key => labelFor(lanes, key) : null })
		: [{ key: '__all', value: null, label: null, all: true }];
	const laneEntries = laneList.map(lane => ({ ...lane, count: 0, cells: new Map(columns.map(column => [column.key, []])) }));
	const laneByKey = new Map(laneEntries.map(lane => [lane.key, lane]));
	const columnByKey = new Map(columns.map(column => [column.key, column]));
	(items || []).forEach((item) => {
		const columnKey = columnKeyOf(by, item, columns);
		const laneKey = lanes ? columnKeyOf(lanes, item, laneList) : '__all';
		const lane = laneByKey.get(laneKey);
		lane.cells.get(columnKey).push(item);
		lane.count += 1;
		columnByKey.get(columnKey).count += 1;
	});
	return { columns, lanes: laneEntries };
}

/** The items in the order the board shows them: column by column, lane by lane within each. */
export function boardOrder(layout) {
	const out = [];
	(layout?.columns || []).forEach((column) => {
		(layout?.lanes || []).forEach((lane) => {
			(lane.cells.get(column.key) || []).forEach(item => out.push(item));
		});
	});
	return out;
}

/**
 * The value a column means for an item of a card whose definition is `def`:
 * the choice's value, a person's or a record's id, '' for Other. A list
 * element (never offered) or a choice this item's card does not list is not
 * a place the item can go: `undefined`.
 */
export function columnValueFor(def, column) {
	if (!def || !column) return undefined;
	if (column.other) return '';
	if (def.dataType === 'choice') {
		const listed = choiceListOf(def).some(choice => String(choice !== null && typeof choice === 'object' ? choice.value : choice) === column.value);
		return listed ? column.value : undefined;
	}
	return column.value;
}

/** Does this item already sit in `column` (or lane)? Read as the board reads it. */
function sitsIn(element, def, item, column, columns) {
	if (Array.isArray(columns) && columns.length) return columnKeyOf(element, item, columns) === column.key;
	const key = valueKey(element, item?.elements?.[element?.name]);
	if (column.other) {
		// Other holds the blank values and, on a choice, one the list no longer names.
		return !key || (element?.dataType === 'choice' && columnValueFor(def, { key, value: key }) === undefined);
	}
	return key === valueKey(element, column.value);
}

/**
 * What moving `item` into (lane, column) writes: `{ elements }` holding only
 * what changes, `null` when nothing would, or `{ refused }` with the reason
 * when this item cannot go there (its card does not list that choice).
 *
 * An element whose column (or lane) the item already sits in is never
 * written: dropping a card back where it was changes nothing, and a move
 * across columns leaves its lane alone — even when that lane is Other and
 * the value there is one the list no longer names. `columns` and
 * `laneColumns` are the board's own (`layoutBoard`); without them the item's
 * place is read from its value.
 */
export function movePatch({ item, def, laneDef = null, by, lanes = null, column, lane = null, columns = null, laneColumns = null }) {
	const elements = {};
	if (!sitsIn(by, def, item, column, columns)) {
		const value = columnValueFor(def, column);
		if (value === undefined) {
			return { refused: `${column?.label || 'That column'} is not one of the choices for this item.` };
		}
		elements[by.name] = value;
	}
	if (lanes && lane && !lane.all && !sitsIn(lanes, laneDef, item, lane, laneColumns)) {
		const laneValue = columnValueFor(laneDef, lane);
		if (laneValue === undefined) {
			return { refused: `${lane.label || 'That lane'} is not one of the choices for this item.` };
		}
		elements[lanes.name] = laneValue;
	}
	return Object.keys(elements).length ? { elements } : null;
}

/** The column `delta` places from this item's, or null at either end. */
export function neighbourColumn(layout, by, item, delta) {
	const columns = layout?.columns || [];
	const at = columns.findIndex(column => column.key === columnKeyOf(by, item, columns));
	const next = columns[at + delta];
	return at >= 0 && next ? next : null;
}

// ---- the address -------------------------------------------------------

/** The board the address asks for: `?board=<element>&lanes=<element>`. */
export function boardFromParams(searchParams) {
	const by = searchParams?.get?.('board') || '';
	const lanes = searchParams?.get?.('lanes') || '';
	return { by: by || null, lanes: lanes || null };
}

const boardKey = collectionId => `keepr.itemsBoard.${collectionId || ''}`;

/** The board a person last drew this collection by (a per-viewer convenience). */
export function readStoredBoard(collectionId) {
	try {
		const raw = window.localStorage.getItem(boardKey(collectionId));
		const parsed = raw ? JSON.parse(raw) : null;
		if (!parsed || typeof parsed !== 'object') return { by: null, lanes: null };
		return {
			by: typeof parsed.by === 'string' && parsed.by ? parsed.by : null,
			lanes: typeof parsed.lanes === 'string' && parsed.lanes ? parsed.lanes : null,
		};
	} catch {
		return { by: null, lanes: null };
	}
}

export function writeStoredBoard(collectionId, { by = null, lanes = null } = {}) {
	try {
		window.localStorage.setItem(boardKey(collectionId), JSON.stringify({ by, lanes }));
	} catch { /* per-viewer convenience only */ }
}

/** The saved filter's `board` for what is on screen, or null. */
export function storedBoardOf(board) {
	if (!board?.by?.name) return null;
	return board.lanes?.name ? { by: board.by.name, lanes: board.lanes.name } : { by: board.by.name };
}
